import React, { useState, useEffect, useCallback, useRef } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { URL } from "../config";

ChartJS.register(ArcElement, Tooltip, Legend);

const ENQUIRY_URL  = `${URL}/enquiries`;
const FOLLOWUP_URL = `${URL}/followup-masters`;
const EMP_URL      = `${URL}/employee-masters`;
const STATUS_URL   = `${URL}/lead-status-masters`;

if (typeof document !== "undefined" && !document.getElementById("poppins-font")) {
  const link = document.createElement("link");
  link.id   = "poppins-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

const BR = "#1D4ED8";
const ROWS_PER_PAGE = 10;

function getStatusKey(statusName) {
  if (!statusName) return "new";
  const n = statusName.toLowerCase();
  if (n.includes("follow"))  return "follow";
  if (n.includes("success")) return "success";
  if (n.includes("cancel"))  return "cancel";
  return "new";
}

const STATUS_META = {
  follow:  { label: "Follow Up",     color: "#D97706", bg: "#FEF9C3", text: "#854D0E" },
  success: { label: "Success Close", color: "#16A34A", bg: "#DCFCE7", text: "#166534" },
  cancel:  { label: "Cancel",        color: "#DC2626", bg: "#FEE2E2", text: "#991B1B" },
  new:     { label: "New",           color: "#7C3AED", bg: "#F5F3FF", text: "#6D28D9" },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function Avatar({ name, size = 28 }) {
  const colors = ["#1D4ED8","#7C3AED","#DB2777","#D97706","#059669","#DC2626"];
  let hash = 0;
  for (let i = 0; i < (name||"").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const bg = colors[Math.abs(hash) % colors.length];
  const parts = (name || "?").trim().split(/\s+/);
  const initials = parts.length === 1 ? parts[0][0] : parts[0][0] + parts[parts.length-1][0];
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", justifyContent:"center",
      width:size, height:size, borderRadius:"50%",
      background:bg, color:"#fff", fontSize:size*0.38,
      fontWeight:700, fontFamily:"'Poppins',sans-serif", flexShrink:0,
    }}>
      {initials.toUpperCase()}
    </span>
  );
}

function StatusBadge({ statusKey }) {
  const m = STATUS_META[statusKey] || STATUS_META.new;
  return (
    <span style={{
      background:m.bg, color:m.text, padding:"2px 9px",
      borderRadius:20, fontSize:11, fontWeight:700,
      display:"inline-flex", alignItems:"center", gap:5,
      fontFamily:"'Poppins',sans-serif", whiteSpace:"nowrap",
    }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:m.color }} />
      {m.label}
    </span>
  );
}

function Skeleton() {
  return (
    <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:14 }}>
      <div style={sk(36,36,50)} />
      <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
        <div style={sk("40%",12)} />
        <div style={sk("60%",10)} />
      </div>
      <div style={sk(80,22,20)} />
    </div>
  );
}
function sk(w, h, r = 6) {
  return {
    width:w, height:h, borderRadius:r,
    background:"linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)",
    backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite",
  };
}

function EmployeeDropdown({ employees, selectedId, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = employees.filter(e =>
    (e.name||"").toLowerCase().includes(search.toLowerCase())
  );
  const selected = selectedId ? employees.find(e => e.id === selectedId) : null;

  return (
    <div ref={ref} style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <label style={lblStyle}>Created By</label>
      <div style={{ position:"relative" }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            height:40, padding:"0 36px 0 12px", borderRadius:8,
            border:`1.5px solid ${selectedId ? BR : "#E2E8F0"}`,
            background:"#fff", cursor:"pointer", fontSize:13,
            fontFamily:"'Poppins',sans-serif",
            color:selectedId ? BR : "#64748B",
            fontWeight:selectedId ? 700 : 500,
            textAlign:"left", minWidth:200,
            backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
            backgroundRepeat:"no-repeat", backgroundPosition:"right 10px center",
            display:"flex", alignItems:"center", gap:8, overflow:"hidden",
          }}
        >
          {selected
            ? <><Avatar name={selected.name} size={22} /><span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{selected.name}</span></>
            : <span>All Employees</span>
          }
        </button>

        {open && (
          <div style={{
            position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:1000,
            background:"#fff", borderRadius:10, border:"1.5px solid #E2E8F0",
            minWidth:260, boxShadow:"0 8px 24px rgba(0,0,0,.13)", overflow:"hidden",
          }}>
            <div style={{ padding:"8px 10px", borderBottom:"1px solid #F1F5F9" }}>
              <input autoFocus type="text" placeholder="Search employee..." value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width:"100%", height:32, border:"1.5px solid #E2E8F0", borderRadius:6, padding:"0 8px", fontSize:12.5, fontFamily:"'Poppins',sans-serif", outline:"none", boxSizing:"border-box", color:"#1E293B" }}
              />
            </div>
            <div
              onClick={() => { onChange(null); setOpen(false); setSearch(""); }}
              style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 12px", cursor:"pointer", background:!selectedId ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F1F5F9" }}
            >
              <span style={{ fontSize:16 }}>👥</span>
              <span style={{ fontSize:13, fontWeight:!selectedId ? 700 : 500, color:!selectedId ? BR : "#1E293B", fontFamily:"'Poppins',sans-serif" }}>All Employees</span>
              {!selectedId && <span style={{ marginLeft:"auto", fontSize:11, color:BR, fontWeight:700 }}>✓</span>}
            </div>
            <div style={{ maxHeight:240, overflowY:"auto" }}>
              {filtered.length === 0
                ? <div style={{ padding:14, fontSize:12, color:"#94A3B8", textAlign:"center" }}>No results</div>
                : filtered.map(emp => {
                  const isSel = selectedId === emp.id;
                  return (
                    <div key={emp.id}
                      onClick={() => { onChange(emp.id); setOpen(false); setSearch(""); }}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", cursor:"pointer", background:isSel ? "#EFF6FF" : "transparent", borderBottom:"1px solid #F8FAFC" }}
                    >
                      <Avatar name={emp.name} size={28} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:isSel ? 700 : 500, color:isSel ? BR : "#1E293B", fontFamily:"'Poppins',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{emp.name}</div>
                        {emp.desig && <div style={{ fontSize:11, color:"#94A3B8" }}>{emp.desig}</div>}
                      </div>
                      {isSel && <span style={{ fontSize:11, color:BR, fontWeight:700 }}>✓</span>}
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PieCard({ title, counts, activeKey, onSliceClick }) {
  const keys  = Object.keys(counts).filter(k => counts[k] > 0);
  const total = keys.reduce((s, k) => s + counts[k], 0);

  if (total === 0) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>{title}</div>
        <div style={{ height:200, display:"flex", alignItems:"center", justifyContent:"center", color:"#94A3B8", fontSize:13 }}>No data</div>
      </div>
    );
  }

  const chartData = {
    labels: keys.map(k => STATUS_META[k].label),
    datasets: [{
      data: keys.map(k => counts[k]),
      backgroundColor: keys.map(k => STATUS_META[k].color),
      borderColor: "#ffffff",
      borderWidth: 3,
      hoverOffset: 10,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => {
            const pct = total ? Math.round(ctx.raw / total * 100) : 0;
            return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
          },
        },
      },
    },
    onClick: (evt, elements) => {
      if (!elements.length) { onSliceClick(null); return; }
      const key = keys[elements[0].index];
      onSliceClick(activeKey === key ? null : key);
    },
  };

  return (
    <div style={cardStyle}>
      <div style={cardTitleStyle}>{title}</div>
      <div style={{ position:"relative", height:200 }}>
        <Pie data={chartData} options={options} />
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, justifyContent:"center", marginTop:10 }}>
        {keys.map(k => {
          const m   = STATUS_META[k];
          const pct = Math.round(counts[k] / total * 100);
          const isActive = activeKey === k;
          return (
            <button
              key={k}
              onClick={() => onSliceClick(activeKey === k ? null : k)}
              style={{
                display:"flex", alignItems:"center", gap:5,
                background: isActive ? m.bg : "transparent",
                border:`1.5px solid ${isActive ? m.color : "#E2E8F0"}`,
                borderRadius:20, padding:"3px 10px", cursor:"pointer",
                fontFamily:"'Poppins',sans-serif", fontSize:11, fontWeight:600,
                color: isActive ? m.text : "#64748B",
                transition:"all .15s",
              }}
            >
              <span style={{ width:8, height:8, borderRadius:"50%", background:m.color, flexShrink:0 }} />
              {m.label}: {counts[k]} ({pct}%)
            </button>
          );
        })}
      </div>
      {activeKey && (
        <div style={{ textAlign:"center", fontSize:11, color:BR, marginTop:6, fontWeight:600 }}>
          Showing: {STATUS_META[activeKey]?.label} — click again to clear
        </div>
      )}
    </div>
  );
}

function Pagination({ currentPage, totalPages, totalRows, onPageChange }) {
  if (totalPages <= 1) return null;

  const from = (currentPage - 1) * ROWS_PER_PAGE + 1;
  const to   = Math.min(currentPage * ROWS_PER_PAGE, totalRows);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);

  const btnBase = {
    width:32, height:32, borderRadius:8,
    fontSize:13, fontWeight:600, cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"center",
    fontFamily:"'Poppins',sans-serif", transition:"all .12s",
  };

  return (
    <div style={{
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"10px 16px", borderTop:"1px solid #F1F5F9",
      fontFamily:"'Poppins',sans-serif", flexWrap:"wrap", gap:8,
    }}>
      <div style={{ fontSize:12, color:"#64748B", fontWeight:500 }}>
        Affichage {from}–{to} sur {totalRows} enregistrement{totalRows !== 1 ? "s" : ""}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
        {/* Première page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="Première page"
          style={{ ...btnBase, border:"1.5px solid #E2E8F0", background:currentPage===1?"#F8FAFC":"#fff", color:currentPage===1?"#CBD5E1":"#475569", cursor:currentPage===1?"default":"pointer", fontSize:15 }}
        >«</button>

        {/* Précédent */}
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          title="Page précédente"
          style={{ ...btnBase, border:"1.5px solid #E2E8F0", background:currentPage===1?"#F8FAFC":"#fff", color:currentPage===1?"#CBD5E1":"#475569", cursor:currentPage===1?"default":"pointer", fontSize:16 }}
        >‹</button>

        {pages.map((item, idx) =>
          item === "..." ? (
            <span key={`ellipsis-${idx}`} style={{ padding:"0 4px", color:"#94A3B8", fontSize:13, userSelect:"none" }}>…</span>
          ) : (
            <button
              key={item}
              onClick={() => onPageChange(item)}
              style={{ ...btnBase, border:`1.5px solid ${currentPage===item ? BR : "#E2E8F0"}`, background:currentPage===item ? BR : "#fff", color:currentPage===item ? "#fff" : "#475569", fontWeight:currentPage===item ? 700 : 600 }}
            >{item}</button>
          )
        )}

        {/* Suivant */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          title="Page suivante"
          style={{ ...btnBase, border:"1.5px solid #E2E8F0", background:currentPage===totalPages?"#F8FAFC":"#fff", color:currentPage===totalPages?"#CBD5E1":"#475569", cursor:currentPage===totalPages?"default":"pointer", fontSize:16 }}
        >›</button>

        {/* Dernière page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Dernière page"
          style={{ ...btnBase, border:"1.5px solid #E2E8F0", background:currentPage===totalPages?"#F8FAFC":"#fff", color:currentPage===totalPages?"#CBD5E1":"#475569", cursor:currentPage===totalPages?"default":"pointer", fontSize:15 }}
        >»</button>
      </div>
    </div>
  );
}

const cardStyle = {
  background:"#fff", borderRadius:12,
  border:"1px solid #E2E8F0",
  padding:"16px 18px",
  boxShadow:"0 1px 6px rgba(7,40,119,.06)",
  fontFamily:"'Poppins',sans-serif",
};
const cardTitleStyle = {
  fontSize:13, fontWeight:700, color:"#64748B",
  textAlign:"center", marginBottom:10,
  letterSpacing:"0.3px", textTransform:"uppercase",
};
const lblStyle = {
  fontSize:11, fontWeight:700, color:"#374151",
  letterSpacing:"0.5px", textTransform:"uppercase",
};

export default function EnquiryPieChart() {
  const [allEnquiries, setAllEnquiries] = useState([]);
  const [empMap, setEmpMap]             = useState({});
  const [empList, setEmpList]           = useState([]);
  const [lsMap, setLsMap]               = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const [fromDate, setFromDate]               = useState("");
  const [toDate, setToDate]                   = useState("");
  const [createdByFilter, setCreatedByFilter] = useState(null);

  const [filtered, setFiltered]   = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const enqF = encodeURIComponent(JSON.stringify({ where:{ isActive:1 }, order:["enquiryId DESC"] }));
      const fuF  = encodeURIComponent(JSON.stringify({ where:{ isActive:1 }, order:["followupId DESC"] }));
      const empF = encodeURIComponent(JSON.stringify({ where:{ isActive:1 } }));
      const lsF  = encodeURIComponent(JSON.stringify({ where:{ isActive:1 } }));

      const [enqRes, fuRes, empRes, lsRes] = await Promise.all([
        fetch(`${ENQUIRY_URL}?filter=${enqF}`),
        fetch(`${FOLLOWUP_URL}?filter=${fuF}`),
        fetch(`${EMP_URL}?filter=${empF}`),
        fetch(`${STATUS_URL}?filter=${lsF}`),
      ]);

      if (!enqRes.ok) throw new Error(`Server error ${enqRes.status}`);

      const [enqData, fuData, empData, lsData] = await Promise.all([
        enqRes.json(),
        fuRes.ok ? fuRes.json() : [],
        empRes.ok ? empRes.json() : [],
        lsRes.ok ? lsRes.json() : [],
      ]);

      const eMap = {}; const eList = [];
      (Array.isArray(empData) ? empData : []).forEach(e => {
        const id    = e.employeeId ?? e.id;
        const name  = e.employeeName ?? e.name ?? `Emp #${id}`;
        const desig = e.designation ?? e.designationName ?? "";
        if (id != null) { eMap[id] = { name, desig }; eList.push({ id, name, desig }); }
      });
      setEmpMap(eMap);
      eList.sort((a, b) => a.name.localeCompare(b.name));
      setEmpList(eList);

      const lMap = {};
      (Array.isArray(lsData) ? lsData : []).forEach(l => {
        const id   = l.leadstatusId ?? l.statusId ?? l.leadStatusId ?? l.id;
        const name = l.leadstatusName ?? l.statusName ?? l.leadStatusName ?? l.name ?? "";
        if (id != null) lMap[id] = name;
      });
      setLsMap(lMap);

      const fuArr = Array.isArray(fuData) ? fuData : [];
      const merged = (Array.isArray(enqData) ? enqData : []).map(e => ({
        ...e,
        followups: fuArr.filter(f => f.fkenquiryId === e.enquiryId),
      }));
      setAllEnquiries(merged);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const applyFilters = useCallback(() => {
    setActiveKey(null);
    setCurrentPage(1);
    let result = allEnquiries;

    if (createdByFilter) {
      result = result.filter(e => {
        const cId = e.createdBy ?? e.fkCreatedBy ?? e.createdById;
        return cId === createdByFilter;
      });
    }
    if (fromDate) {
      result = result.filter(e => {
        const d = e.enquiryDate ?? e.createdAt ?? e.date;
        return d && new Date(d) >= new Date(fromDate);
      });
    }
    if (toDate) {
      result = result.filter(e => {
        const d = e.enquiryDate ?? e.createdAt ?? e.date;
        return d && new Date(d) <= new Date(toDate + "T23:59:59");
      });
    }

    setFiltered(result);
  }, [allEnquiries, createdByFilter, fromDate, toDate]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const resetFilters = () => {
    setFromDate(""); setToDate(""); setCreatedByFilter(null);
    setActiveKey(null); setCurrentPage(1);
  };

  const handleSliceClick = (key) => {
    setActiveKey(key);
    setCurrentPage(1);
  };

  const buildCounts = (data) => {
    const counts = { follow:0, success:0, cancel:0, new:0 };
    data.forEach(e => {
      const fu = e.followups?.[0];
      const statusName = fu?.fkLeadStatusId ? (lsMap[fu.fkLeadStatusId] || "") : "";
      counts[getStatusKey(statusName)]++;
    });
    return counts;
  };

  const overallCounts   = buildCounts(filtered);
  const empCounts       = createdByFilter ? buildCounts(filtered) : null;
  const selectedEmpName = createdByFilter ? (empMap[createdByFilter]?.name || `#${createdByFilter}`) : null;

  const tableRows = activeKey
    ? filtered.filter(e => {
        const fu = e.followups?.[0];
        const statusName = fu?.fkLeadStatusId ? (lsMap[fu.fkLeadStatusId] || "") : "";
        return getStatusKey(statusName) === activeKey;
      })
    : filtered;

  // Pagination
  const totalPages    = Math.ceil(tableRows.length / ROWS_PER_PAGE);
  const paginatedRows = tableRows.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const stats = {
    total:   filtered.length,
    follow:  overallCounts.follow,
    success: overallCounts.success,
    cancel:  overallCounts.cancel,
    new:     overallCounts.new,
  };

  return (
    <div style={{ width:"100%", minHeight:"100vh", background:"#F1F5F9", fontFamily:"'Poppins',sans-serif", paddingBottom:40 }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        .trow { transition:background .12s; cursor:default; }
        .trow:hover { background:#EFF6FF !important; }
        .date-inp {
          height:40px; border:1.5px solid #E2E8F0; border-radius:8px;
          padding:0 10px; font-size:13px; font-family:'Poppins',sans-serif;
          color:#1E293B; outline:none; background:#fff;
        }
        .date-inp:focus { border-color:#1D4ED8; }
        .pag-btn:hover:not(:disabled) { background:#EFF6FF !important; border-color:#BFDBFE !important; color:#1D4ED8 !important; }
      `}</style>

      {/* Header */}
      <div style={{
        background:"#0d1b3e",
        backgroundImage:`repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px),
          repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px)`,
        padding:"0 28px", display:"flex", alignItems:"center",
        justifyContent:"space-between", minHeight:72, flexWrap:"wrap", gap:12,
      }}>
        <div style={{ padding:"14px 0" }}>
          <div style={{ fontSize:9.5, fontWeight:600, letterSpacing:"2px", textTransform:"uppercase", color:"rgba(148,163,184,.65)", marginBottom:3 }}>LEAD MANAGEMENT</div>
          <div style={{ fontSize:17, fontWeight:700, color:"#fff" }}>🥧 Enquiry Analytics</div>
        </div>
        <button
          onClick={loadAll}
          style={{ background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.2)", color:"#fff", padding:"8px 18px", borderRadius:50, fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'Poppins',sans-serif" }}
        >🔄 Refresh</button>
      </div>

      <div style={{ padding:"18px 16px" }}>

        {/* Filter Bar */}
        <div style={{ background:"#fff", borderRadius:12, padding:"14px 18px", marginBottom:14, display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-end", boxShadow:"0 1px 6px rgba(7,40,119,.07)" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={lblStyle}>From Date</label>
            <input type="date" className="date-inp" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{ borderColor:fromDate?BR:"#E2E8F0" }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={lblStyle}>To Date</label>
            <input type="date" className="date-inp" value={toDate} min={fromDate||undefined} onChange={e=>setToDate(e.target.value)} style={{ borderColor:toDate?BR:"#E2E8F0" }} />
          </div>

          <EmployeeDropdown employees={empList} selectedId={createdByFilter} onChange={setCreatedByFilter} />

          {(fromDate||toDate||createdByFilter) && (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <label style={{ fontSize:11, color:"transparent" }}>x</label>
              <button onClick={resetFilters} style={{ height:40, padding:"0 16px", borderRadius:8, border:"1.5px solid #FECACA", background:"#FEF2F2", color:"#991B1B", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Poppins',sans-serif" }}>
                ✕ Clear
              </button>
            </div>
          )}
          <div style={{ marginLeft:"auto", fontSize:13, color:"#64748B", fontWeight:600, alignSelf:"flex-end", paddingBottom:2 }}>
            {filtered.length} record{filtered.length!==1?"s":""}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:12, padding:20, textAlign:"center", color:"#991B1B", marginBottom:14 }}>
            <div style={{ fontSize:32, marginBottom:6 }}>⚠️</div>
            <div style={{ fontWeight:700 }}>API Error</div>
            <div style={{ fontSize:12.5, opacity:.8, marginBottom:10 }}>{error}</div>
            <button onClick={loadAll} style={{ padding:"7px 20px", borderRadius:50, background:"#DC2626", color:"#fff", border:"none", fontWeight:700, cursor:"pointer", fontFamily:"'Poppins',sans-serif" }}>🔄 Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ background:"#fff", borderRadius:12, padding:24, boxShadow:"0 1px 6px rgba(7,40,119,.06)", marginBottom:14 }}>
            {[...Array(5)].map((_,i) => <Skeleton key={i} />)}
          </div>
        )}

        {/* Stat Cards */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:14 }}>
            {[
              { label:"Total",         val:stats.total,   bg:"#EFF6FF", color:BR,        icon:"📋" },
              { label:"New",           val:stats.new,     bg:"#F5F3FF", color:"#6D28D9", icon:"🆕" },
              { label:"Follow Up",     val:stats.follow,  bg:"#FEF9C3", color:"#854D0E", icon:"🔁" },
              { label:"Success Close", val:stats.success, bg:"#DCFCE7", color:"#166534", icon:"✅" },
              { label:"Cancelled",     val:stats.cancel,  bg:"#FEE2E2", color:"#991B1B", icon:"❌" },
            ].map((c,i) => (
              <div key={i} style={{ background:c.bg, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
                <span style={{ fontSize:20 }}>{c.icon}</span>
                <div>
                  <div style={{ fontSize:22, fontWeight:800, color:c.color, lineHeight:1.1 }}>{c.val}</div>
                  <div style={{ fontSize:11, color:"#64748B", fontWeight:600 }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pie Charts */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:createdByFilter ? "1fr 1fr" : "1fr", gap:14, marginBottom:14, animation:"fadeIn .3s ease" }}>
            <PieCard
              title="Overall Breakdown"
              counts={overallCounts}
              activeKey={activeKey}
              onSliceClick={handleSliceClick}
            />
            {createdByFilter && empCounts && (
              <PieCard
                title={`${selectedEmpName} — Created By`}
                counts={empCounts}
                activeKey={activeKey}
                onSliceClick={handleSliceClick}
              />
            )}
          </div>
        )}

        {/* No data */}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ background:"#fff", borderRadius:12, padding:"48px 24px", textAlign:"center", color:"#94A3B8", boxShadow:"0 1px 6px rgba(7,40,119,.06)" }}>
            <div style={{ fontSize:44, marginBottom:10 }}>🔍</div>
            <div style={{ fontSize:15, fontWeight:700, color:"#64748B" }}>No records found</div>
            <div style={{ fontSize:12.5, marginTop:4 }}>Try changing filters</div>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ background:"#fff", borderRadius:12, boxShadow:"0 2px 12px rgba(7,40,119,.07)", overflow:"hidden", animation:"fadeIn .3s ease" }}>

            {/* Table Header */}
            <div style={{ padding:"12px 16px", borderBottom:"1px solid #F1F5F9", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:13.5, fontWeight:700, color:"#1E293B" }}>
                {activeKey ? `${STATUS_META[activeKey]?.label} Records` : "All Records"}
                {createdByFilter && selectedEmpName && (
                  <span style={{ color:"#94A3B8", fontWeight:500, fontSize:13 }}> — {selectedEmpName}</span>
                )}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {activeKey && (
                  <button
                    onClick={() => { setActiveKey(null); setCurrentPage(1); }}
                    style={{ background:"#FEF2F2", border:"1px solid #FECACA", color:"#991B1B", borderRadius:20, padding:"3px 10px", fontSize:11.5, fontWeight:700, cursor:"pointer", fontFamily:"'Poppins',sans-serif" }}
                  >✕ Clear filter</button>
                )}
                <span style={{ fontSize:12.5, color:"#64748B", fontWeight:600 }}>{tableRows.length} records</span>
              </div>
            </div>

            {/* Table Body */}
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:860 }}>
                <thead>
                  <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E2E8F0" }}>
                    {["#","Customer","Mobile","Enquiry Date","Source","Budget","Areas","Status","Created By"].map(h => (
                      <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748B", letterSpacing:"0.5px", textTransform:"uppercase", whiteSpace:"nowrap", fontFamily:"'Poppins',sans-serif" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((e, idx) => {
                    const globalIdx  = (currentPage - 1) * ROWS_PER_PAGE + idx + 1;
                    const c          = e.customer || {};
                    const fu         = e.followups?.[0];
                    const statusName = fu?.fkLeadStatusId ? (lsMap[fu.fkLeadStatusId] || "") : "";
                    const sKey       = getStatusKey(statusName);
                    const createdId  = e.createdBy ?? e.fkCreatedBy ?? e.createdById;
                    const createdNm  = createdId ? (empMap[createdId]?.name || `#${createdId}`) : null;
                    const dateField  = e.enquiryDate ?? e.createdAt ?? e.date;
                    const areas      = e.areas?.map(a => a.areaName).join(", ") || null;

                    return (
                      <tr key={e.enquiryId || idx} className="trow" style={{ borderBottom:"1px solid #F1F5F9" }}>
                        <td style={{ padding:"9px 12px", fontSize:12, color:"#94A3B8", fontWeight:600 }}>{globalIdx}</td>
                        <td style={{ padding:"9px 12px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <Avatar name={c.customerName} size={28} />
                            <div>
                              <div style={{ fontWeight:700, fontSize:12.5, color:"#1E293B" }}>{c.customerName||"—"}</div>
                              <div style={{ fontSize:10.5, color:"#94A3B8" }}>{c.mobileno||""}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding:"9px 12px", fontSize:12.5, color:"#475569" }}>{c.mobileno||"—"}</td>
                        <td style={{ padding:"9px 12px", fontSize:12.5, color:"#475569", whiteSpace:"nowrap" }}>{fmtDate(dateField)}</td>
                        <td style={{ padding:"9px 12px", fontSize:12.5, color:"#475569" }}>{e.source?.sourceName||"—"}</td>
                        <td style={{ padding:"9px 12px" }}>
                          {e.budget?.budgetName
                            ? <span style={{ background:"#F0FDF4", color:"#166534", padding:"2px 8px", borderRadius:6, fontSize:11.5, fontWeight:700 }}>💰 {e.budget.budgetName}</span>
                            : <span style={{ color:"#CBD5E1", fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:"9px 12px", maxWidth:140 }}>
                          {areas
                            ? <span title={areas} style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:12, color:"#475569" }}>📍 {areas}</span>
                            : <span style={{ color:"#CBD5E1", fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:"9px 12px" }}><StatusBadge statusKey={sKey} /></td>
                        <td style={{ padding:"9px 12px" }}>
                          {createdNm ? (
                            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                              <Avatar name={createdNm} size={24} />
                              <span style={{ fontSize:12, fontWeight:700, color:"#1E293B" }}>{createdNm}</span>
                            </div>
                          ) : <span style={{ color:"#CBD5E1", fontSize:12 }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRows={tableRows.length}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}